import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, Boxes, History, RefreshCw } from 'lucide-react'
import { useProductListAdmin, useProductDetail, useAdjustProductInventory, useInventoryHistory } from '@/lib/productCatalog'
import { userMessage } from '@/api/errors'
import type { InventoryTransactionType, Product, ProductSummary } from '@/types/ecommerce'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import { Modal } from '@/components/ui/modal'
import { useAlertContext } from '@/providers/AlertProvider'

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function InventoryHistoryModal({ product, onClose }: { product: { id: string; name: string; sku: string } | null; onClose: () => void }) {
  const history = useInventoryHistory(product?.id)

  if (!product) return null

  return (
    <Modal isOpen={!!product} onClose={onClose} size="lg">
      <div className="relative w-full overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <div className="mb-4">
          <h4 className="flex items-center gap-2 text-xl font-semibold text-gray-800 dark:text-white/90">
            <History className="h-5 w-5" /> Stock history
          </h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {product.name} ({product.sku})
          </p>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {history.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : history.isError ? (
            <p className="text-sm text-red-600">Failed to load history.</p>
          ) : !history.data || history.data.items.length === 0 ? (
            <p className="text-sm text-gray-500">No stock adjustments recorded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-xs uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                <tr>
                  <th className="pb-2 pr-4">Date</th>
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4">Δ Qty</th>
                  <th className="pb-2 pr-4">New qty</th>
                  <th className="pb-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {history.data.items.map((t) => (
                  <tr key={t.id}>
                    <td className="py-2 pr-4 text-gray-500 dark:text-gray-400">{formatDateTime(t.createdAt)}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-200">{t.type}</td>
                    <td className={`py-2 pr-4 font-medium ${t.quantity < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {t.quantity > 0 ? '+' : ''}
                      {t.quantity}
                    </td>
                    <td className="py-2 pr-4 text-gray-900 dark:text-white">{t.quantityAfter}</td>
                    <td className="py-2 text-gray-500 dark:text-gray-400">{t.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Modal>
  )
}

const TYPE_OPTIONS: { value: InventoryTransactionType; label: string }[] = [
  { value: 'Purchase', label: 'Purchase (restock)' },
  { value: 'Sale', label: 'Sale' },
  { value: 'Adjustment', label: 'Manual adjustment' },
]

function InventoryRow({
  summary,
  onAdjust,
  onViewHistory,
}: {
  summary: ProductSummary
  onAdjust: (product: Product) => void
  onViewHistory: (product: { id: string; name: string; sku: string }) => void
}) {
  const detail = useProductDetail(summary.id)
  // minStockLevel now comes straight off the list response (ProductSummaryDto), not a
  // per-row detail fetch — detail is still needed for variants/the Adjust modal, but the
  // low-stock flag itself no longer flickers "Loading…" while that resolves.
  const minStock = summary.minStockLevel
  const lowStock = summary.quantity <= minStock
  const variants = detail.data?.variants ?? []

  return (
    <>
      <tr className={`border-t border-gray-100 dark:border-gray-700 ${lowStock ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
        <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
          <div className="flex items-center gap-2">
            {lowStock && <AlertTriangle className="w-4 h-4 text-red-500" />}
            {summary.name}
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-gray-500 font-mono">{summary.sku}</td>
        <td className={`px-4 py-3 ${lowStock ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
          {summary.quantity}
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">{minStock}</td>
        <td className="px-4 py-3 text-xs text-gray-500">{summary.status}</td>
        <td className="px-4 py-3">
          <div className="flex justify-end gap-2">
            <button
              onClick={() => onViewHistory({ id: summary.id, name: summary.name, sku: summary.sku })}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              History
            </button>
            <button
              disabled={!detail.data}
              onClick={() => detail.data && onAdjust(detail.data)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
            >
              {detail.isLoading ? 'Loading…' : 'Adjust Stock'}
            </button>
          </div>
        </td>
      </tr>
      {variants.map((v) => {
        const variantLow = v.quantity <= minStock
        return (
          <tr key={v.id} className={`border-t border-gray-50 dark:border-gray-800 ${variantLow ? 'bg-red-50/60 dark:bg-red-900/5' : ''}`}>
            <td className="px-4 py-2 pl-10 text-gray-500 dark:text-gray-400 text-xs">↳ {v.name}</td>
            <td className="px-4 py-2 text-xs text-gray-400 font-mono">{v.sku}</td>
            <td className={`px-4 py-2 text-xs ${variantLow ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500'}`}>{v.quantity}</td>
            <td className="px-4 py-2" />
            <td className="px-4 py-2" />
            <td className="px-4 py-2" />
          </tr>
        )
      })}
    </>
  )
}

function InventoryPage() {
  const [search, setSearch] = useState('')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([])
  const [adjustingProduct, setAdjustingProduct] = useState<Product | null>(null)
  const [historyProduct, setHistoryProduct] = useState<{ id: string; name: string; sku: string } | null>(null)

  const filters = useMemo(() => ({ search: search || undefined, cursor, pageSize: 20 }), [search, cursor])
  const { data, isLoading, isError, error, isFetching, refetch } = useProductListAdmin(filters)
  const adjust = useAdjustProductInventory()
  const { success, error: showError } = useAlertContext()

  const products = data?.items ?? []

  const resetToFirstPage = () => {
    setCursor(undefined)
    setCursorHistory([])
  }
  const goNext = () => {
    if (!data?.nextCursor) return
    setCursorHistory((h) => [...h, cursor])
    setCursor(data.nextCursor)
  }
  const goPrev = () => {
    if (!cursorHistory.length) return
    const prev = cursorHistory[cursorHistory.length - 1]
    setCursorHistory((h) => h.slice(0, -1))
    setCursor(prev)
  }

  const adjustFields: EditField[] = useMemo(() => {
    const base: EditField[] = []
    if (adjustingProduct && adjustingProduct.variants.length > 0) {
      base.push({
        key: 'variantId',
        label: 'Variant',
        type: 'select',
        options: [
          { value: '', label: `Base product (${adjustingProduct.sku})` },
          ...adjustingProduct.variants.map((v) => ({ value: v.id, label: `${v.name} (${v.sku})` })),
        ],
      })
    }
    base.push(
      { key: 'quantityDelta', label: 'Quantity change', type: 'number', required: true, hint: 'Positive to add stock, negative to remove' },
      { key: 'type', label: 'Type', type: 'select', required: true, options: TYPE_OPTIONS },
      { key: 'reason', label: 'Reason', placeholder: 'Optional note' },
    )
    return base
  }, [adjustingProduct])

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Boxes className="w-6 h-6" /> Inventory
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Stock levels and adjustments (ProductService).</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/products"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" /> Products
          </Link>
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <input
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          resetToFirstPage()
        }}
        placeholder="Search products…"
        className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading inventory…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load inventory: {(error as Error)?.message}.</p>
        ) : products.length === 0 ? (
          <p className="p-6 text-gray-500">No products found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Quantity</th>
                <th className="px-4 py-2">Low-stock at</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <InventoryRow key={p.id} summary={p} onAdjust={setAdjustingProduct} onViewHistory={setHistoryProduct} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(cursorHistory.length > 0 || data?.hasMore) && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button disabled={cursorHistory.length === 0} onClick={goPrev} className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40">
            Previous
          </button>
          <button disabled={!data?.hasMore} onClick={goNext} className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40">
            Next
          </button>
        </div>
      )}

      <EditModal
        isOpen={!!adjustingProduct}
        onClose={() => setAdjustingProduct(null)}
        title="Adjust Stock"
        description={adjustingProduct ? `${adjustingProduct.name} (${adjustingProduct.sku})` : undefined}
        fields={adjustFields}
        initialValues={{ variantId: '', type: 'Adjustment' }}
        submitLabel="Apply"
        onSubmit={async (values) => {
          if (!adjustingProduct) return
          try {
            const result = await adjust.mutateAsync({
              id: adjustingProduct.id,
              body: {
                variantId: values.variantId || null,
                quantityDelta: Number(values.quantityDelta),
                type: values.type,
                reason: values.reason || undefined,
              },
            })
            success('Stock adjusted', `New quantity: ${result.quantityAfter}`)
          } catch (err) {
            const message = userMessage(err)
            showError('Adjustment failed', message)
            throw new Error(message)
          }
        }}
      />

      <InventoryHistoryModal product={historyProduct} onClose={() => setHistoryProduct(null)} />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/inventory/')({
  component: InventoryPage,
})
